import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { PageHeader } from '../components/PageHeader';
import { AppDataGrid } from '../components/AppDataGrid';
import { ConfirmDialog, FormDialog } from '../components/Dialogs';
import { useAuth } from '../contexts/AuthContext';
import { ACCESS_LEVELS, errorMessage, formatDateTime, usersApi, type AccessLevel, type User } from '../lib/api';

interface UserForm {
  name: string;
  access_level: AccessLevel;
}

const EMPTY: UserForm = { name: '', access_level: 2 };

// Same rule as the server: these would break the station text file.
const NAME_PATTERN = /[#=]/;

export function Users() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<User | 'new' | null>(null);
  const [form, setForm] = useState<UserForm>(EMPTY);
  const [deleting, setDeleting] = useState<User | null>(null);
  const nameInvalid = NAME_PATTERN.test(form.name);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await usersApi.list());
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: GridColDef<User>[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
    {
      field: 'access_level',
      headerName: 'Access level',
      width: 150,
      valueFormatter: (v) => ACCESS_LEVELS[v as AccessLevel],
    },
    { field: 'created_at', headerName: 'Created', width: 180, valueFormatter: (v) => formatDateTime(v as string) },
  ];
  if (isAdmin) {
    columns.push({
      field: 'actions',
      headerName: '',
      width: 96,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              aria-label={`Edit ${row.name}`}
              onClick={() => {
                setForm({ name: row.name, access_level: row.access_level });
                setEditing(row);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" aria-label={`Delete ${row.name}`} onClick={() => setDeleting(row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    });
  }

  return (
    <>
      <PageHeader
        title="Users"
        actions={
          isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setForm(EMPTY);
                setEditing('new');
              }}
            >
              New user
            </Button>
          )
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <AppDataGrid rows={rows} columns={columns} loading={loading} localeText={{ noRowsLabel: 'No users yet' }} />

      <FormDialog
        open={editing !== null}
        title={editing === 'new' ? 'New user' : 'Edit user'}
        onClose={() => setEditing(null)}
        onSubmit={async () => {
          if (editing === 'new') await usersApi.create(form);
          else if (editing) await usersApi.update(editing.id, form);
          await load();
        }}
      >
        <TextField
          label="Full name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          error={nameInvalid}
          helperText={nameInvalid ? "Can't contain # or =" : 'Must be unique'}
          required
          autoFocus
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <TextField
          select
          label="Access level"
          value={form.access_level}
          onChange={(e) => setForm({ ...form, access_level: Number(e.target.value) as AccessLevel })}
        >
          <MenuItem value={1}>{ACCESS_LEVELS[1]}</MenuItem>
          <MenuItem value={2}>{ACCESS_LEVELS[2]}</MenuItem>
        </TextField>
      </FormDialog>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete user?"
        message={`Delete ${deleting?.name}?`}
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await usersApi.remove(deleting.id);
          await load();
        }}
      />
    </>
  );
}
